// Core React Imports
import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Local Imports
import ApiService from "../../data/ApiService";
import UtilsUnit from "../../utils/UtilsUnit";
import { useLoading } from "../../widgets/loadings/WidgetLoading";
import { useWidgetFilterDropdowns } from "../../widgets/dropdowns/widget-filter-dropdowns/useWidgetFilterDropdowns";
import useUtilsUnit from "../../utils/useUtilsUnit";
import ConstantsImages from "../../constants/ConstantsImages";
import ConstantsTexts from "../../constants/ConstantsTexts";
import { DropdownType } from "../../widgets/dropdowns/widget-dropdown/WidgetDropdown";
import ConstantsParams from "../../constants/ConstantsParams";
import PolygonUploadUtils, { ERROR_MESSAGES } from "../../utils/PolygonUploadUtils";
import { PENDING_CHANGE_REQUESTS_CHANGED_EVENT } from "../../widgets/shared_widgets/widget-change-requests-icon/WidgetChangeRequestsIcon";

export function usePageTerminalInfo() {
  const location = useLocation();
  const terminalID = location.state?.terminalID;
  const createNewTerminalModeFromState = location.state?.createNewTerminalMode;
  const changeRequestFromState = location.state?.changeRequest || null;

  const navigate = useNavigate();
  const { userData } = useUtilsUnit({ userData: true });
  const [filterData, setFilterData] = useState(null);
  const { showLoading, hideLoading } = useLoading();
  const [data, setData] = useState([]);
  const [dataIsReceived, setDataIsReceived] = useState(false);
  const [showUpdatesList, setShowUpdatesList] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [createNewTerminalMode, setCreateNewTerminalMode] = useState(false);
  const [isDialogEditOpen, setDialogEditOpen] = useState(false);
  const [isFrozenTerminalBlockedDialogOpen, setIsFrozenTerminalBlockedDialogOpen] = useState(false);
  const [isSubjectiveChangeReasonDialogOpen, setIsSubjectiveChangeReasonDialogOpen] = useState(false);
  const [isSubjectiveChangeSuccessDialogOpen, setIsSubjectiveChangeSuccessDialogOpen] = useState(false);
  const [isOpenChangeRequestBlockedDialogOpen, setIsOpenChangeRequestBlockedDialogOpen] = useState(false);
  const [subjectiveChangeSuccessKind, setSubjectiveChangeSuccessKind] = useState("create");
  const [pendingChangeRequest, setPendingChangeRequest] = useState(null);
  const [isChangeRequestDetailsOpen, setIsChangeRequestDetailsOpen] = useState(false);
  // After approve/reject, hide source score + comparison colors (persists across remount via location.state)
  const [showAcceptedTerminalPresentation, setShowAcceptedTerminalPresentation] = useState(
    !!location.state?.acceptedTerminalPresentation
  );
  const [isApprovingChangeRequest, setIsApprovingChangeRequest] = useState(false);
  const [isRejectingChangeRequest, setIsRejectingChangeRequest] = useState(false);
  const [uploadedPolygon, setUploadedPolygon] = useState(null);
  const [showSearchTerminal, setShowSearchTerminal] = useState(false);
  const [colorEditMode, setColorEditMode] = useState("#5051A1");
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [busLinesMenuOpened, setBusLinesMenuOpened] = useState(false);
  const [busLinesInTerminalChecked, setBusLinesInTerminalChecked] = useState(false);
  const [busLinesInTerminalExpanded, setBusLinesInTerminalExpanded] = useState(false);
  const [busLinesNearTerminalChecked, setBusLinesNearTerminalChecked] = useState(false);
  const [busLinesNearTerminalExpanded, setBusLinesNearTerminalExpanded] = useState(false);
  const [busLinesInTerminalItems, setBusLinesInTerminalItems] = useState([]);
  const [busLinesNearTerminalItems, setBusLinesNearTerminalItems] = useState([]);
  const [checkedBusLinesInTerminalItems, setCheckedBusLinesInTerminalItems] = useState([]);
  const [checkedBusLinesNearTerminalItems, setCheckedBusLinesNearTerminalItems] = useState([]);
  const [selectedModelVersion, setSelectedModelVersion] = useState(null);
  const [selectedTerminalVersion, setSelectedTerminalVersion] = useState(null);
  const [isCreatingNewTerminalVersion, setIsCreatingNewTerminalVersion] = useState(false);
  const [isBaseModelSelected, setIsBaseModelSelected] = useState(false);
  const [dropdownType, setDropdownType] = useState(DropdownType.DEFAULT_SELECT);

  // Data from API
  const [nationalModelForTerminalData, setNationalModelForTerminalData] = useState({});
  const [nationalModelForTerminalCount, setNationalModelForTerminalCount] = useState({});
  const [secondaryMeasure, setSecondaryMeasure] = useState(null);
  const [changedTerminalID, setChangedTerminalID] = useState(null);
  const [terminalOwnership, setTerminalOwnership] = useState([]);
  const [municipalityPosition, setMunicipalityPosition] = useState([]);
  const [terminalTypesAndPrices, setTerminalTypesAndPrices] = useState([]);
  const [nationalModelForTerminalDistinctWorkneed, setNationalModelForTerminalDistinctWorkneed] = useState([]);
  const [urgencyMeasure, setUrgencyMeasure] = useState([]);
  const [organizationInCharge, setOrganizationInCharge] = useState([]);
  const [budgetStatus, setBudgetStatus] = useState([]);
  const [terminalStatus, setTerminalStatus] = useState([]);
  const [terminalStops, setTerminalStops] = useState([]);
  const [modelVersions, setModelVersions] = useState([]);
  const [terminalVersions, setTerminalVersions] = useState([]);
  const [modelTerminalVersions, setModelTerminalVersions] = useState([]);

  // Input
  const [inputValueTerminalMakat, setInputValueTerminalMakat] = useState("");
  const [inputValueTerminalMakatErez, setInputValueTerminalMakatErez] = useState("");
  const [inputValueTerminalName, setInputValueTerminalName] = useState("");
  const [inputValueRequiredTerminalSize, setInputValueRequiredTerminalSize] = useState("");
  const [inputValueImportanceMeasure, setInputValueImportanceMeasure] = useState("");
  const [inputValuePotentialMeasure, setInputValuePotentialMeasure] = useState("");
  const [inputValueAreaInPlaningArea, setInputValueAreaInPlaningArea] = useState("");
  const [inputValueAreaNeedForPlaningArea, setInputValueAreaNeedForPlaningArea] = useState("");
  const [inputValueComments, setInputValueComments] = useState("");

  // Dropdown
  const [selectedTerminalTypeName, setSelectedTerminalTypeName] = useState(null);
  const [selectedTerminalOwnership, setSelectedTerminalOwnership] = useState(null);
  const [selectedMunicipalityPositionPlace, setSelectedMunicipalityPositionPlace] = useState(null);
  const [selectedWorkNeed, setSelectedWorkNeed] = useState(null);
  const [selectedUrgencyMeasureValue, setSelectedUrgencyMeasureValue] = useState(null);
  const [selectedOrganizationInChargeName, setSelectedOrganizationInChargeName] = useState(null);
  const [selectedBudgetStatusName, setSelectedBudgetStatusName] = useState(null);
  const [selectedTerminalStatusName, setSelectedTerminalStatusName] = useState(null);
  const [selectedRingByStrategicPlan, setSelectedRingByStrategicPlan] = useState(null);
  const [selectedApplicability, setSelectedApplicability] = useState(null);

  // Utility arrays
  const numbers1To4 = UtilsUnit.generateNumberArray(1, 4);
  const numbers1To5 = UtilsUnit.generateNumberArray(1, 5);

  const jsonEditRef = useRef({});
  const jsonEditRefCheckChanges = useRef({});
  const showLoadingRef = useRef(showLoading);
  const hideLoadingRef = useRef(hideLoading);
  const lastActionRef = useRef(null);
  const terminalVersionToDeleteRef = useRef(null);
  const baseTerminalSelectedVersionRef = useRef(null);
  const originalSelectedTerminalVersionRef = useRef(null);

  // useWidgetFilterDropdowns - start
  const { uniqueDistricts, uniqueMunicipalities, uniqueTerminals } = useWidgetFilterDropdowns(filterData, selectedDistrict, selectedMunicipality);
  // useWidgetFilterDropdowns - end

  // Set createNewTerminalMode when location state changes
  useEffect(() => {
    if (createNewTerminalModeFromState) {
      setCreateNewTerminalMode(true);
      setEditMode(true);
    }
  }, [createNewTerminalModeFromState]);

  // Keep pending change-request context when navigating from the manager popup
  useEffect(() => {
    let isMounted = true;

    const syncPendingChangeRequest = async () => {
      if (!changeRequestFromState?.requestID) {
        if (isMounted) {
          setPendingChangeRequest(null);
          setIsChangeRequestDetailsOpen(false);
        }
        return;
      }

      try {
        const pendingRequests = await ApiService.getPendingChangeRequests();
        const stillPending = pendingRequests.some(
          (request) => request.requestID === changeRequestFromState.requestID && request.isApproved == null
        );

        if (!isMounted) {
          return;
        }

        if (stillPending) {
          const freshRequest = pendingRequests.find(
            (request) => request.requestID === changeRequestFromState.requestID && request.isApproved == null
          );
          setShowAcceptedTerminalPresentation(false);
          setPendingChangeRequest(freshRequest ?? changeRequestFromState);
        } else {
          setPendingChangeRequest(null);
          setShowAcceptedTerminalPresentation(true);
          navigate(location.pathname, {
            replace: true,
            state: {
              terminalID: changeRequestFromState.requestedTerminalID || terminalID,
              acceptedTerminalPresentation: true,
            },
          });
        }
      } catch (error) {
        console.error("Error verifying pending change request:", error);
        if (isMounted) {
          setPendingChangeRequest(changeRequestFromState);
        }
      } finally {
        if (isMounted) {
          setIsChangeRequestDetailsOpen(false);
        }
      }
    };

    syncPendingChangeRequest();

    return () => {
      isMounted = false;
    };
  }, [changeRequestFromState, location.state?.timestamp]);

  // Fetch data on component mount
  useEffect(() => {
    (async () => {
      await fetchFilterData();
      await fetchData(terminalID, 0);
      setDataIsReceived(true);
    })();
  }, [location.state]);

  // Reset dropdown selections every time the page is visited
  useEffect(() => {
    setSelectedModelVersion(null);
    setSelectedTerminalVersion(null);
  }, [location.state?.timestamp]);

  // Set initial selected versions when versions data is loaded
  useEffect(() => {
    if (modelVersions && modelVersions.length > 0 && !selectedModelVersion) {
      // Select the first model version by default
      setSelectedModelVersion(modelVersions[0]);
    }

    if (terminalVersions && terminalVersions.length > 0 && !selectedTerminalVersion) {
      // Select the first terminal version by default
      setSelectedTerminalVersion(terminalVersions[0]);
    }
  }, [modelVersions, terminalVersions, selectedModelVersion, selectedTerminalVersion]);

  // Fetch filter data function
  const fetchFilterData = async () => {
    showLoadingRef.current();

    try {
      const filterDataResponse = await ApiService.getDataForFiltering();
      setFilterData(filterDataResponse);
    } catch (error) {
      console.error("Error fetching filter data:", error);
    } finally {
      hideLoadingRef.current();
    }
  };

  // Fetches terminal data from API and processes the response
  const fetchData = async (selectedTerminalID, modelVersion) => {
    showLoadingRef.current();

    try {
      const fetchedData = await ApiService.getTerminalData(selectedTerminalID, modelVersion);
      if (fetchedData?.terminalNotInFrozenVersion) {
        showFrozenTerminalBlockedDialog();
        return false;
      }
      handleFechedAPIData(fetchedData);
      return true;
    } catch (error) {
      console.error("Error fetching data:", error);
      return false;
    } finally {
      hideLoadingRef.current();
    }
  };

  // Processes fetched API data by setting state, populating inputs, and resetting UI components
  function handleFechedAPIData(fetchedData) {
    setData(fetchedData);
    setDataFromFetchedData(fetchedData);
    setDataToInputs(fetchedData);
    setJsonEditRefCheckChangesData();
    resetBusLinesInTerminalLogic();
    setShowSearchTerminal(false);
    clearBaseModel();
  }

  function setDataFromFetchedData(fetchedData) {
    setNationalModelForTerminalData(fetchedData.nationalModelForTerminal_data);
    setNationalModelForTerminalCount(fetchedData.nationalModelForTerminal_count);
    setSecondaryMeasure(fetchedData.secondaryMeasure);
    setChangedTerminalID(fetchedData.changedTerminalID);
    setTerminalStops(fetchedData.terminalStops);
    setModelTerminalVersions(fetchedData?.modelTerminalVersions);
    setModelVersionsAndTerminalVersions(fetchedData);
    setTerminalOwnership(fetchedData.terminalOwnership);
    setMunicipalityPosition(fetchedData.municipality);
    setTerminalTypesAndPrices(fetchedData.terminalTypesAndPrices);
    setNationalModelForTerminalDistinctWorkneed(fetchedData.nationalModelForTerminal_distinct_workneed);
    setUrgencyMeasure(fetchedData.urgencyMeasure);
    setOrganizationInCharge(fetchedData.organizationInCharge);
    setBudgetStatus(fetchedData.budgetStatus);
    setTerminalStatus(fetchedData.terminalStatus);
  }

  // Creates distinct model and terminal versions from fetched data to populate dropdown options
  function setModelVersionsAndTerminalVersions(fetchedData) {
    if (fetchedData?.modelTerminalVersions) {
      // Create distinct model versions
      const distinctModelVersions = [];
      const seenModelVersions = new Set();

      fetchedData.modelTerminalVersions.forEach((item) => {
        if (!seenModelVersions.has(item.model_version_display)) {
          seenModelVersions.add(item.model_version_display);
          distinctModelVersions.push({
            modelVersionInt: item.modelVersionInt,
            model_version_display: item.model_version_display,
            terminalID: item.terminalID,
            isFrozen: !!item.isFrozen,
          });
        }
      });

      setModelVersions(distinctModelVersions);

      // Create distinct terminal versions
      const distinctTerminalVersions = [];
      const seenTerminalVersions = new Set();

      fetchedData.modelTerminalVersions.forEach((item) => {
        if (!seenTerminalVersions.has(item.terminal_version_display)) {
          seenTerminalVersions.add(item.terminal_version_display);
          distinctTerminalVersions.push({
            terminalVersionInt: item.terminalVersionInt,
            terminal_version_display: item.terminal_version_display,
            terminalID: item.terminalID,
          });
        }
      });

      setTerminalVersions(distinctTerminalVersions);
    }
  }

  function setDataToInputs(data) {
    var nationalModelForTerminalData = data.nationalModelForTerminal_data;
    var modelTerminalVersions = data.modelTerminalVersions;

    // Input
    setInputValueRequiredTerminalSize(nationalModelForTerminalData.sizeNeededForTerminalInAcre);
    setInputValueImportanceMeasure(nationalModelForTerminalData.numberOfTerminalsInArea);
    setInputValuePotentialMeasure(nationalModelForTerminalData.populationDensity2040);
    setInputValueAreaInPlaningArea(nationalModelForTerminalData.areaInPlaningArea);
    setInputValueAreaNeedForPlaningArea(nationalModelForTerminalData.areaNeedForPlaningArea);
    setInputValueComments(nationalModelForTerminalData.comments);

    // Dropdown
    setSelectedTerminalTypeName({
      terminalTypeName: nationalModelForTerminalData.terminalType,
    });
    setSelectedTerminalOwnership({
      terminalOwnershipName: nationalModelForTerminalData.terminalOwnership,
    });
    setSelectedMunicipalityPositionPlace({
      municipalityName: nationalModelForTerminalData.municipalityName,
    });
    setSelectedWorkNeed({
      terminalNewStatusNameInNmft: nationalModelForTerminalData.terminalsNewStatus,
    });
    setSelectedUrgencyMeasureValue(
      data.urgencyMeasure?.find((item) => item.urgencyMeasureValue === nationalModelForTerminalData.yearOfNeedGrade) || {
        urgencyMeasureValue: nationalModelForTerminalData.yearOfNeedGrade,
      }
    );
    setSelectedOrganizationInChargeName({
      organizationInChargeName: nationalModelForTerminalData.organizationInCharge,
    });
    setSelectedBudgetStatusName({
      budgetStatusName: nationalModelForTerminalData.budgetStatus,
    });
    setSelectedTerminalStatusName({
      terminalStatusName: nationalModelForTerminalData.terminalStatus,
    });
    setSelectedRingByStrategicPlan({
      number: nationalModelForTerminalData.ringByStrategicPlan,
    });
    setSelectedApplicability({
      number:
        nationalModelForTerminalData.municipalityPosition ??
        nationalModelForTerminalData.municipalityPositionMeasure,
    });
    setSelectedModelVersionFromInt(nationalModelForTerminalData.modelVersionInt, modelTerminalVersions);
    setSelectedTerminalVersionFromInt(nationalModelForTerminalData.terminalVersionInt, modelTerminalVersions);

    // Bus Lines
    setDataToBusLines(data.terminalLines);

    // JSON
    jsonEditRef.current = {
      year_of_need_grade: nationalModelForTerminalData.yearOfNeedGrade,
      number_of_terminals_in_area: nationalModelForTerminalData.numberOfTerminalsInArea,
      population_density_2040: nationalModelForTerminalData.populationDensity2040,
      area_in_planning_area: nationalModelForTerminalData.areaInPlaningArea,
      area_need_for_planning_area: nationalModelForTerminalData.areaNeedForPlaningArea,
      size_needed_for_terminal_acre: nationalModelForTerminalData.sizeNeededForTerminalInAcre,
      ring_by_strategic_plan: nationalModelForTerminalData.ringByStrategicPlan,
      municipality_position:
        nationalModelForTerminalData.municipalityPosition ??
        nationalModelForTerminalData.municipalityPositionMeasure,
      terminal_type: nationalModelForTerminalData.terminalType,
      terminal_ownership: nationalModelForTerminalData.terminalOwnership,
      municipality_position_place: nationalModelForTerminalData.municipalityName,
      work_need: nationalModelForTerminalData.workNeed,
      comments: nationalModelForTerminalData.comments,
      organization_in_charge: nationalModelForTerminalData.organizationInCharge,
      budget_status: nationalModelForTerminalData.budgetStatus,
      status: nationalModelForTerminalData.terminalStatus,
    };
  }

  // useWidgetFilterDropdowns - start
  const handleSelectDropdownDistrict = (option) => {
    setSelectedDistrict(option);
    setSelectedMunicipality(null);
    setSelectedTerminal(null);
  };

  const handleSelectDropdownMunicipality = (option) => {
    setSelectedMunicipality(option);
    setSelectedTerminal(null);
  };

  const handleSelectDropdownTerminal = (option) => {
    setSelectedTerminal(option);
  };
  // useWidgetFilterDropdowns - end

  const handleSearchTerminalIconClick = () => {
    if (editMode) return;

    setShowSearchTerminal(!showSearchTerminal);
  };

  const handleShowUpdatesListClick = (value) => {
    setShowUpdatesList(value);
  };

  const handleSetEditModeTrueClick = async () => {
    const wouldSubmitSubjectiveChangeRequest =
      UtilsUnit.editorUser(userData) &&
      !createNewTerminalMode &&
      !isCreatingNewTerminalVersion &&
      isTerminalVersionZero();

    if (wouldSubmitSubjectiveChangeRequest) {
      const terminalID = getTerminalIDZero() || getAppropriateTerminalVersion()?.terminalID;
      showLoadingRef.current();

      try {
        const hasOpenRequest = await terminalHasOpenChangeRequest(terminalID);
        if (hasOpenRequest) {
          showOpenChangeRequestBlockedDialog();
          return;
        }
      } catch (error) {
        console.error("Error checking open change requests:", error);
      } finally {
        hideLoadingRef.current();
      }
    }

    handleSetEditMode(true);
  };

  const handleCancelTerminalChanges = () => {
    if (UtilsUnit.areJSONsEqualLoose(jsonEditRefCheckChanges.current, jsonEditRef.current)) {
      if (createNewTerminalMode) {
        navigate(-1);
      } else {
        handleSetEditMode(false);
        clearBaseModelAndSetOriginalSelectedTerminalVersion();
      }
    } else {
      // Set last action to cancel and show confirmation dialog
      lastActionRef.current = ConstantsParams.ActionTypes.CANCEL;
      showEditAlertDialog();
    }
  };

  const showEditAlertDialog = () => {
    setDialogEditOpen(true);
  };

  const closeEditAlertDialog = (cancelEdit) => {
    setDialogEditOpen(false);

    if (cancelEdit) {
      // User confirmed the action
      if (lastActionRef.current === ConstantsParams.ActionTypes.BASE_MODEL) {
        // User confirmed base model change - proceed with base model change
        handleConfirmBaseModelChange();
      } else if (lastActionRef.current === ConstantsParams.ActionTypes.CANCEL) {
        if (createNewTerminalMode) {
          navigate(-1);
        } else {
          // User confirmed cancel - handle based on create new version state
          handleSetEditMode(false);
          clearBaseModel();
          setDataToInputs(data);
        }
      } else if (lastActionRef.current === ConstantsParams.ActionTypes.UPLOAD_POLYGON) {
        // User confirmed polygon upload - proceed with file selection
        handlePolygonUploadConfirm();
      } else if (lastActionRef.current === ConstantsParams.ActionTypes.DELETE) {
        // User confirmed terminal deletion - proceed with deletion
        handleDeleteTerminalOrVersion(nationalModelForTerminalData.terminalID, false);
      } else if (lastActionRef.current === ConstantsParams.ActionTypes.DELETE_VERSION) {
        // User confirmed terminal version deletion - proceed with deletion
        handleDeleteTerminalOrVersion(terminalVersionToDeleteRef.current?.terminalID, true);
      }
    }
  };

  // Separates bus lines into two categories based on whether they start in terminal or are near terminal
  function setDataToBusLines(terminalLines) {
    const inTerminal = [];
    const nearTerminal = [];

    for (const item of terminalLines) {
      const lineWithCheck = { ...item, checked: false };

      if (item.isstartinterminal === 1) {
        inTerminal.push(lineWithCheck);
      } else {
        nearTerminal.push(lineWithCheck);
      }
    }

    setBusLinesInTerminalItems(inTerminal);
    setBusLinesNearTerminalItems(nearTerminal);
  }

  // Resets all bus lines related state to initial values
  function resetBusLinesInTerminalLogic() {
    setBusLinesMenuOpened(false);
    setCheckedBusLinesInTerminalItems([]);
    setCheckedBusLinesNearTerminalItems([]);
    setBusLinesInTerminalChecked(false);
    setBusLinesNearTerminalChecked(false);
    setBusLinesInTerminalExpanded(false);
    setBusLinesNearTerminalExpanded(false);
  }

  // Toggles edit mode and updates UI color, resets new version creation state when exiting edit mode
  function handleSetEditMode(value) {
    setEditMode(value);
    setColorEditMode(value ? "#5A81FA" : "#5051A1");

    // Reset creating new terminal version state when exiting edit mode
    if (!value) {
      setIsCreatingNewTerminalVersion(false);
    }
  }

  function setJsonEditRefCheckChangesData() {
    jsonEditRefCheckChanges.current = { ...jsonEditRef.current };
  }

  const handleSelectTerminalTypeName = (option) => {
    handleSelectOption(option, "terminalTypeName", setSelectedTerminalTypeName, "terminal_type");
  };

  const handleSelectTerminalOwnership = (option) => {
    handleSelectOption(option, "terminalOwnershipName", setSelectedTerminalOwnership, "terminal_ownership");
  };

  const handleSelectMunicipalityPositionPlace = (option) => {
    handleSelectOption(option, "municipalityName", setSelectedMunicipalityPositionPlace, "municipality_position_place");
  };

  const handleSelectWorkNeed = (option) => {
    handleSelectOption(option, "terminalNewStatusNameInNmft", setSelectedWorkNeed, "work_need");
  };

  const handleSelectUrgencyMeasureValue = (option) => {
    setSelectedUrgencyMeasureValue({
      urgencyMeasureValue: option.urgencyMeasureValue,
      urgencyMeasureGrade: option.urgencyMeasureGrade,
    });
    jsonEditRef.current.year_of_need_grade = option.urgencyMeasureValue;
  };

  const handleSelectOrganizationInChargeName = (option) => {
    handleSelectOption(option, "organizationInChargeName", setSelectedOrganizationInChargeName, "organization_in_charge");
  };

  const handleSelectBudgetStatusName = (option) => {
    handleSelectOption(option, "budgetStatusName", setSelectedBudgetStatusName, "budget_status");
  };

  const handleSelectTerminalStatusName = (option) => {
    handleSelectOption(option, "terminalStatusName", setSelectedTerminalStatusName, "status");
  };

  const handleSelectRingByStrategicPlan = (option) => {
    handleSelectOption(option, "number", setSelectedRingByStrategicPlan, "ring_by_strategic_plan");
  };

  const handleSelectApplicability = (option) => {
    handleSelectOption(option, "number", setSelectedApplicability, "municipality_position");
  };

  const handleChangeTerminalMakat = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValueTerminalMakat, "terminal_makat");
  };

  const handleChangeTerminalMakatErez = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValueTerminalMakatErez, "terminal_makat_erez");
  };

  const handleChangeTerminalName = (event) => {
    handleChangeSimple(event, setInputValueTerminalName, "terminal_name");
  };

  const handleChangeRequiredTerminalSize = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexDecimalOneNumber, setInputValueRequiredTerminalSize, "size_needed_for_terminal_acre", 150);
  };

  const handleChangeImportanceMeasure = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValueImportanceMeasure, "number_of_terminals_in_area");
  };

  const handleChangePotentialMeasure = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexInt, setInputValuePotentialMeasure, "population_density_2040");
  };

  const handleChangeAreaInPlaningArea = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexDecimalOneNumber, setInputValueAreaInPlaningArea, "area_in_planning_area");
  };

  const handleChangeAreaNeedForPlaningArea = (event) => {
    handleChangeRegexNComma(event, UtilsUnit.regexDecimalOneNumber, setInputValueAreaNeedForPlaningArea, "area_need_for_planning_area");
  };

  const handleChangeComments = (event) => {
    handleChangeSimple(event, setInputValueComments, "comments");
  };

  const showFrozenTerminalBlockedDialog = () => {
    setIsFrozenTerminalBlockedDialogOpen(true);
  };

  const closeFrozenTerminalBlockedDialog = () => {
    setIsFrozenTerminalBlockedDialogOpen(false);
  };

  // Handles model version selection and fetches corresponding terminal data
  const handleSelectModelVersion = async (option) => {
    if (option?.isFrozen) {
      const terminalVersionInt = selectedTerminalVersion?.terminalVersionInt ?? 0;
      const hasFrozenSnapshot = modelTerminalVersions.some(
        (item) =>
          Number(item.modelVersionInt) === Number(option.modelVersionInt) &&
          Number(item.terminalVersionInt) === Number(terminalVersionInt) &&
          !!item.isFrozen
      );

      if (createNewTerminalMode || !hasFrozenSnapshot) {
        showFrozenTerminalBlockedDialog();
        return;
      }
    }

    const previousModelVersion = selectedModelVersion;
    setSelectedModelVersion(option);

    // Find the terminalID based on selected model and terminal versions
    const selectedTerminalID = findTerminalID(option.modelVersionInt, selectedTerminalVersion?.terminalVersionInt);

    if (selectedTerminalID) {
      const ok = await fetchData(selectedTerminalID, option.modelVersionInt);
      if (!ok && option?.isFrozen) {
        setSelectedModelVersion(previousModelVersion);
      }
    }
  };

  // Handles terminal version selection with support for base model mode and regular mode
  const handleSelectTerminalVersion = async (option) => {
    if (option === null) {
      if (dropdownType === DropdownType.SINGLE_SELECT) {
        baseTerminalSelectedVersionRef.current = null;
      } else {
        setSelectedTerminalVersion(null);
      }
      return;
    }

    // If in SINGLE_SELECT mode (base model selected), store in ref instead of state
    if (dropdownType === DropdownType.SINGLE_SELECT) {
      baseTerminalSelectedVersionRef.current = option;
    } else {
      setSelectedTerminalVersion(option);
      // Find the terminalID based on selected model and terminal versions
      const selectedTerminalID = findTerminalID(selectedModelVersion?.modelVersionInt, option.terminalVersionInt);

      if (selectedTerminalID) {
        fetchData(selectedTerminalID, selectedModelVersion?.modelVersionInt);
      }
    }
  };

  // Finds terminal ID by matching model version and terminal version combinations
  const findTerminalID = (modelVersionInt, terminalVersionInt) => {
    const matchingItem = modelTerminalVersions.find((item) => item.modelVersionInt === modelVersionInt && item.terminalVersionInt === terminalVersionInt);
    return matchingItem?.terminalID;
  };

  const setSelectedModelVersionFromInt = (modelVersionInt, modelTerminalVersionsData) => {
    const matchingModelVersion = modelTerminalVersionsData?.find((item) => item.modelVersionInt === modelVersionInt);

    if (matchingModelVersion) {
      setSelectedModelVersion({
        modelVersionInt: matchingModelVersion.modelVersionInt,
        model_version_display: matchingModelVersion.model_version_display,
        terminalID: matchingModelVersion.terminalID,
        isFrozen: !!matchingModelVersion.isFrozen,
      });
    }
  };

  const setSelectedTerminalVersionFromInt = (terminalVersionInt, modelTerminalVersionsData) => {
    const matchingTerminalVersion = modelTerminalVersionsData?.find((item) => item.terminalVersionInt === terminalVersionInt);

    if (matchingTerminalVersion) {
      setSelectedTerminalVersion({
        terminalVersionInt: matchingTerminalVersion.terminalVersionInt,
        terminal_version_display: matchingTerminalVersion.terminal_version_display,
        terminalID: matchingTerminalVersion.terminalID,
      });
    }
  };

  const handleSelectOption = (option, key, setInputValue, jsonKey) => {
    const selectedObj = {
      [key]: option[key],
    };

    setInputValue(selectedObj);

    jsonEditRef.current[jsonKey] = option[key];
  };

  // Handles input changes with regex validation, comma removal, and optional maximum value validation
  const handleChangeRegexNComma = (event, regex, setInputValue, jsonKey, maxValue = null) => {
    const value = event.target.value;

    // Remove commas from the input value
    const valueWithoutComma = UtilsUnit.removeCommas(value);

    // Validate the cleaned value
    if (regex.test(valueWithoutComma)) {
      // Check max value if provided
      if (maxValue !== null) {
        const numericValue = parseFloat(valueWithoutComma);

        if (numericValue > maxValue) {
          return; // Don't update if value exceeds max
        }
      }

      // Update state with the cleaned value
      setInputValue(valueWithoutComma);

      // Update the corresponding JSON field
      jsonEditRef.current[jsonKey] = valueWithoutComma;
    }
  };

  const handleChangeSimple = (event, setInputValue, jsonKey) => {
    const value = event.target.value;

    setInputValue(value);

    jsonEditRef.current[jsonKey] = value;
  };

  // Runs all validation checks in sequence, returns false if any validation fails
  function handleValidations() {
    var isValid = true;

    if (!handleFirstValidation()) {
      isValid = false;
    } else if (!handleSecondValidation()) {
      isValid = false;
    } else if (createNewTerminalMode && !handleThirdValidation()) {
      isValid = false;
    }

    return isValid;
  }

  // Validates that existing area is less than required area
  function handleFirstValidation() {
    var isValid = true;

    var errorFirstLine = ConstantsTexts.CODE_163;

    const existingArea = Number(inputValueAreaInPlaningArea);
    const requiredArea = Number(inputValueAreaNeedForPlaningArea);

    if (existingArea >= requiredArea) {
      alert(errorFirstLine);

      isValid = false;
    }

    return isValid;
  }

  // Validates required dropdown fields for createNewTerminalMode (excluding organizationInCharge, budgetStatus, and terminalStatus)
  function handleSecondValidation() {
    var isValid = true;

    // Check required dropdown fields only when they are not disabled
    if (!isEditorEditingInitialVersion() && (!selectedTerminalTypeName || !selectedTerminalTypeName.terminalTypeName)) {
      alert(ConstantsTexts.CODE_197);
      isValid = false;
    } else if (
      isCreatingOrEditingInitialVersion() &&
      !isEditorEditingInitialVersion() &&
      (!selectedMunicipalityPositionPlace || !selectedMunicipalityPositionPlace.municipalityName)
    ) {
      alert(ConstantsTexts.CODE_199);
      isValid = false;
    } else if (!shouldShowFieldsForGeneral() && (!selectedWorkNeed || !selectedWorkNeed.terminalNewStatusNameInNmft)) {
      alert(ConstantsTexts.CODE_235);
      isValid = false;
    } else if (!shouldShowFieldsForNonInitialTerminalAndModelVersion() && (!selectedUrgencyMeasureValue || !selectedUrgencyMeasureValue.urgencyMeasureValue)) {
      alert(ConstantsTexts.CODE_200);
      isValid = false;
    } else if (!selectedApplicability || !selectedApplicability.number) {
      alert(ConstantsTexts.CODE_202);
      isValid = false;
    } else if (!isEditorEditingInitialVersion() && (!selectedRingByStrategicPlan || !selectedRingByStrategicPlan.number)) {
      alert(ConstantsTexts.CODE_201);
      isValid = false;
    }

    return isValid;
  }

  function handleThirdValidation() {
    var isValid = true;

    if (!jsonEditRef.current.polygon) {
      alert(ConstantsTexts.CODE_203);
      isValid = false;
    } else if (!validateTerminalMakatUniqueness()) {
      isValid = false;
    }

    return isValid;
  }

  // Validates that terminal_makat and terminal_makat_erez are unique in the system
  function validateTerminalMakatUniqueness() {
    if (!filterData || !Array.isArray(filterData)) {
      return true; // If no filter data, skip validation
    }

    const terminalMakat = inputValueTerminalMakat.trim();
    const terminalMakatErez = inputValueTerminalMakatErez.trim();

    // Check if terminal_makat (erezSKU) already exists
    if (terminalMakat) {
      const existingTerminalMakat = filterData.find((item) => item.erezSKU && item.erezSKU.toString() === terminalMakat);

      if (existingTerminalMakat) {
        alert(ConstantsTexts.CODE_208.replace("{terminalMakat}", terminalMakat));
        return false;
      }
    }

    // Check if terminal_makat_erez (erezSKU) already exists
    if (terminalMakatErez) {
      const existingTerminalMakatErez = filterData.find((item) => item.erezSKU && item.erezSKU.toString() === terminalMakatErez);

      if (existingTerminalMakatErez) {
        alert(ConstantsTexts.CODE_209.replace("{terminalMakatErez}", terminalMakatErez));
        return false;
      }
    }

    return true;
  }

  const shouldSubmitSubjectiveChangeRequest = () => {
    return (
      UtilsUnit.editorUser(userData) &&
      !createNewTerminalMode &&
      !isCreatingNewTerminalVersion &&
      isEditingInitialTerminalVersion()
    );
  };

  const getUrgencyMeasureGrade = () => {
    if (selectedUrgencyMeasureValue?.urgencyMeasureGrade != null) {
      return Number(selectedUrgencyMeasureValue.urgencyMeasureGrade);
    }

    const urgencyValue = selectedUrgencyMeasureValue?.urgencyMeasureValue ?? jsonEditRef.current.year_of_need_grade;
    if (urgencyValue == null || urgencyValue === "") {
      return null;
    }

    const numericGrade = Number(urgencyValue);
    if (!Number.isNaN(numericGrade) && Number.isInteger(numericGrade)) {
      return numericGrade;
    }

    const measure = urgencyMeasure.find((item) => item.urgencyMeasureValue === urgencyValue);
    return measure?.urgencyMeasureGrade != null ? Number(measure.urgencyMeasureGrade) : null;
  };

  const getSubjectiveChangeSuccessMessage = () => {
    const terminalName =
      pendingChangeRequest?.terminalName ||
      inputValueTerminalName ||
      nationalModelForTerminalData?.terminalName ||
      "";
    const erezSKU =
      pendingChangeRequest?.erezSKU ||
      inputValueTerminalMakatErez ||
      nationalModelForTerminalData?.erezSKU ||
      "";

    if (subjectiveChangeSuccessKind === "approve") {
      return ConstantsTexts.CODE_279.replace("{terminalName}", terminalName).replace("{erezSKU}", erezSKU);
    }

    if (subjectiveChangeSuccessKind === "reject") {
      return ConstantsTexts.CODE_281.replace("{terminalName}", terminalName).replace("{erezSKU}", erezSKU);
    }

    return ConstantsTexts.CODE_264.replace("{terminalName}", terminalName).replace("{erezSKU}", erezSKU);
  };

  const closeSubjectiveChangeReasonDialog = () => {
    setIsSubjectiveChangeReasonDialogOpen(false);
  };

  const closeOpenChangeRequestBlockedDialog = () => {
    setIsOpenChangeRequestBlockedDialogOpen(false);
  };

  const getOpenChangeRequestBlockedTerminalName = () => {
    return inputValueTerminalName || nationalModelForTerminalData?.terminalName || "";
  };

  const terminalHasOpenChangeRequest = async (terminalID) => {
    if (!terminalID) {
      return false;
    }

    const pendingRequests = await ApiService.getPendingChangeRequests();
    return pendingRequests.some((request) => request.baseTerminalID === terminalID);
  };

  const showOpenChangeRequestBlockedDialog = () => {
    setIsSubjectiveChangeReasonDialogOpen(false);
    setIsOpenChangeRequestBlockedDialogOpen(true);
  };

  const closeSubjectiveChangeSuccessDialog = async () => {
    setIsSubjectiveChangeSuccessDialogOpen(false);

    if (subjectiveChangeSuccessKind === "approve" || subjectiveChangeSuccessKind === "reject") {
      setPendingChangeRequest(null);
      navigate(location.pathname, {
        replace: true,
        state: {
          terminalID: location.state?.terminalID || terminalID,
          acceptedTerminalPresentation: true,
          timestamp: Date.now(),
        },
      });
      return;
    }

    await fetchData(getAppropriateTerminalVersion()?.terminalID, selectedModelVersion?.modelVersionInt);
    handleSetEditMode(false);
    setIsCreatingNewTerminalVersion(false);
  };

  const submitSubjectiveChangeRequest = async (reason) => {
    if (!reason?.trim()) {
      alert(ConstantsTexts.CODE_265);
      return false;
    }

    const terminalID = getTerminalIDZero() || getAppropriateTerminalVersion()?.terminalID;
    const newUrgency = getUrgencyMeasureGrade();
    const newFeasibility = Number(selectedApplicability?.number ?? jsonEditRef.current.municipality_position);
    const userID = UtilsUnit.getUserID(userData);
    const userName = UtilsUnit.getUserName(userData);

    if (!terminalID || !newUrgency || !newFeasibility || !userID || !userName) {
      alert(ConstantsTexts.CODE_261);
      return false;
    }

    showLoadingRef.current();

    try {
      const response = await ApiService.createTerminalSubjectiveChangeRequest({
        terminalID,
        newUrgency,
        newFeasibility,
        reason: reason.trim(),
        userID,
        userName,
      });

      if (response?.status === 200) {
        setIsSubjectiveChangeReasonDialogOpen(false);
        setSubjectiveChangeSuccessKind("create");
        setIsSubjectiveChangeSuccessDialogOpen(true);
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error saving subjective change request:", error);
      if (error?.response?.status === 409) {
        showOpenChangeRequestBlockedDialog();
      }
      return false;
    } finally {
      hideLoadingRef.current();
    }
  };

  // Validates form data and sends terminal updates to API with user info and appropriate action type
  const handleSaveTerminalChanges = async () => {
    if (!handleValidations()) return;

    if (createNewTerminalMode && selectedModelVersion?.isFrozen) {
      showFrozenTerminalBlockedDialog();
      return;
    }

    if (shouldSubmitSubjectiveChangeRequest()) {
      const newUrgency = getUrgencyMeasureGrade();
      const newFeasibility = selectedApplicability?.number ?? jsonEditRef.current.municipality_position;

      if (!newUrgency) {
        alert(ConstantsTexts.CODE_200);
        return;
      }

      if (!newFeasibility) {
        alert(ConstantsTexts.CODE_202);
        return;
      }

      setIsSubjectiveChangeReasonDialogOpen(true);
      return;
    }

    showLoadingRef.current();

    try {
      jsonEditRef.current.terminalID = getAppropriateTerminalVersion()?.terminalID;
      jsonEditRef.current.userID = UtilsUnit.getUserID(userData);
      jsonEditRef.current.userName = UtilsUnit.getUserName(userData);

      setActionType();
      const response = await ApiService.updateTerminalData(jsonEditRef.current);

      if (response.status === 200) {
        if (createNewTerminalMode) {
          UtilsUnit.handleNavigateToTerminalInfo(navigate, false, getTerminalIDToFetch(response));
        } else {
          await fetchData(getTerminalIDToFetch(response), selectedModelVersion?.modelVersionInt);
          handleSetEditMode(false);
          setIsCreatingNewTerminalVersion(false);
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
    } finally {
      hideLoadingRef.current();
    }
  };

  function setActionType() {
    if (createNewTerminalMode) {
      jsonEditRef.current.action = ConstantsParams.ActionTypes.NEW;
    } else if (isCreatingNewTerminalVersion) {
      jsonEditRef.current.action = ConstantsParams.ActionTypes.CREATE;
    } else {
      jsonEditRef.current.action = ConstantsParams.ActionTypes.UPDATE;
    }
  }

  function getTerminalIDToFetch(response) {
    if (createNewTerminalMode || isCreatingNewTerminalVersion) {
      return response.data.terminalID;
    } else {
      return getAppropriateTerminalVersion()?.terminalID;
    }
  }

  function getSecondaryMeasurePercentForDisplay(serial) {
    return UtilsUnit.getValueFromList(serial, secondaryMeasure, "secondaryMeasureSerial", "secondaryMeasurePercentForDisplay");
  }

  function handleBusLinesMenuOpened() {
    setBusLinesMenuOpened(!busLinesMenuOpened);
  }

  // Toggles all bus lines in terminal checkboxes and updates checked items list
  function handleBusLinesInTerminalChecked() {
    const newChecked = !busLinesInTerminalChecked;
    setBusLinesInTerminalChecked(newChecked);
    setBusLinesInTerminalItems((prevItems) => {
      const updatedItems = prevItems.map((item) => ({ ...item, checked: newChecked }));
      setCheckedBusLinesInTerminalItems(updatedItems.filter((item) => item.checked));
      return updatedItems;
    });
  }

  function handleBusLinesInTerminalExpanded() {
    setBusLinesInTerminalExpanded(!busLinesInTerminalExpanded);
  }

  // Toggles all bus lines near terminal checkboxes and updates checked items list
  function handleBusLinesNearTerminalChecked() {
    const newChecked = !busLinesNearTerminalChecked;
    setBusLinesNearTerminalChecked(newChecked);
    setBusLinesNearTerminalItems((prevItems) => {
      const updatedItems = prevItems.map((item) => ({ ...item, checked: newChecked }));
      setCheckedBusLinesNearTerminalItems(updatedItems.filter((item) => item.checked));
      return updatedItems;
    });
  }

  function handleBusLinesNearTerminalExpanded() {
    setBusLinesNearTerminalExpanded(!busLinesNearTerminalExpanded);
  }

  // Toggles individual bus line in terminal checkbox and updates master checkbox state
  function handleTerminalItemCheck(index) {
    setBusLinesInTerminalItems((prev) => {
      const updated = [...prev];
      updated[index].checked = !updated[index].checked;

      const allChecked = updated.every((item) => item.checked);
      setBusLinesInTerminalChecked(allChecked);

      setCheckedBusLinesInTerminalItems(updated.filter((item) => item.checked));
      return updated;
    });
  }

  // Toggles individual bus line near terminal checkbox and updates master checkbox state
  function handleNearTerminalItemCheck(index) {
    setBusLinesNearTerminalItems((prev) => {
      const updated = [...prev];
      updated[index].checked = !updated[index].checked;

      const allChecked = updated.every((item) => item.checked);
      setBusLinesNearTerminalChecked(allChecked);

      setCheckedBusLinesNearTerminalItems(updated.filter((item) => item.checked));
      return updated;
    });
  }

  function getSelectedBusLinesData() {
    return [...checkedBusLinesInTerminalItems, ...checkedBusLinesNearTerminalItems];
  }

  const isTerminalVersionZero = () => {
    return nationalModelForTerminalData.terminalVersionInt === 0;
  };

  const isTerminalVersionGreaterThanZero = () => {
    return nationalModelForTerminalData.terminalVersionInt > 0;
  };

  const isModelVersionGreaterThanZero = () => {
    return nationalModelForTerminalData.modelVersionInt > 0;
  };

  const isTerminalVersionGreaterThanZeroAndEditModeFalse = () => {
    return !editMode && isTerminalVersionGreaterThanZero();
  };

  const isPendingChangeRequestReview = () => {
    return (
      !editMode &&
      !!pendingChangeRequest &&
      pendingChangeRequest.isApproved == null &&
      isTerminalVersionGreaterThanZero() &&
      !showAcceptedTerminalPresentation &&
      !location.state?.acceptedTerminalPresentation
    );
  };

  // «ניקוד מסוף מקור» only while reviewing an open change request — removed after approve/reject
  const shouldShowSourceTerminalScore = () => {
    return isPendingChangeRequestReview();
  };

  const shouldUseTerminalVersionComparisonColors = () => {
    if (showAcceptedTerminalPresentation || location.state?.acceptedTerminalPresentation) {
      return false;
    }
    return isTerminalVersionGreaterThanZeroAndEditModeFalse();
  };

  const hasPendingValueChanged = (beforeValue, afterValue) => {
    if (beforeValue == null || beforeValue === "" || afterValue == null || afterValue === "") {
      return false;
    }
    return Number(beforeValue) !== Number(afterValue);
  };

  const getPendingUrgencyGaugeBefore = () => {
    return (
      pendingChangeRequest?.urgencyMeasureBefore ??
      pendingChangeRequest?.urgencyBefore ??
      null
    );
  };

  /** Live gauge + «היה XX» badge for pending urgency (Figma 129:4152), aligned with feasibilityPositionBefore. */
  const getPendingUrgencyGaugePresentation = () => {
    const liveMeasure = nationalModelForTerminalData?.urgencyMeasure;

    if (!isPendingChangeRequestReview()) {
      return {
        numberText: liveMeasure,
        previousValue: null,
        highlight: false,
      };
    }

    const req = pendingChangeRequest;
    const beforeMeasure = getPendingUrgencyGaugeBefore();
    const proposedMeasure = req?.urgencyMeasureAfter ?? req?.urgencyAfter ?? liveMeasure;
    const numberText = liveMeasure ?? proposedMeasure;
    const highlight = hasPendingUrgencyChanged();

    if (!highlight || beforeMeasure == null || beforeMeasure === "") {
      return { numberText, previousValue: null, highlight };
    }

    let previousValue = null;
    if (numberText != null && Number(beforeMeasure) !== Number(numberText)) {
      previousValue = beforeMeasure;
    } else if (
      req?.urgencyBefore != null &&
      req?.urgencyAfter != null &&
      Number(req.urgencyBefore) !== Number(req.urgencyAfter)
    ) {
      // Live measure may match base; still show badge from request snapshot vs proposed grade.
      previousValue = beforeMeasure;
      if (proposedMeasure != null && Number(proposedMeasure) !== Number(numberText)) {
        return {
          numberText: proposedMeasure,
          previousValue,
          highlight,
        };
      }
    }

    return { numberText, previousValue, highlight };
  };

  const getPendingFeasibilityBefore = () => {
    return pendingChangeRequest?.feasibilityPositionBefore ?? pendingChangeRequest?.feasibilityBefore ?? null;
  };

  const hasPendingYearOfNeedChanged = () => {
    if (!isPendingChangeRequestReview()) {
      return false;
    }
    const before = String(pendingChangeRequest?.yearOfNeedBefore ?? "").trim();
    const after = String(
      pendingChangeRequest?.yearOfNeedAfter ?? nationalModelForTerminalData?.yearOfNeedGrade ?? ""
    ).trim();
    return before !== "" && after !== "" && before !== after;
  };

  const getCurrentFeasibilityScore = () => {
    return (
      nationalModelForTerminalData?.municipalityPosition ??
      nationalModelForTerminalData?.municipalityPositionMeasure ??
      null
    );
  };

  const hasPendingUrgencyChanged = () => {
    if (!isPendingChangeRequestReview()) {
      return false;
    }

    const req = pendingChangeRequest;
    if (!req) {
      return false;
    }

    if (hasPendingYearOfNeedChanged()) {
      return true;
    }

    if (req.urgencyMeasureBefore != null && req.urgencyMeasureAfter != null) {
      return Number(req.urgencyMeasureBefore) !== Number(req.urgencyMeasureAfter);
    }

    if (req.urgencyBefore != null && req.urgencyAfter != null) {
      return Number(req.urgencyBefore) !== Number(req.urgencyAfter);
    }

    return hasPendingValueChanged(getPendingUrgencyGaugeBefore(), nationalModelForTerminalData?.urgencyMeasure);
  };

  const hasPendingFeasibilityChanged = () => {
    return (
      isPendingChangeRequestReview() &&
      hasPendingValueChanged(getPendingFeasibilityBefore(), getCurrentFeasibilityScore())
    );
  };

  const hasPendingPointsChanged = () => {
    return (
      isPendingChangeRequestReview() &&
      hasPendingValueChanged(pendingChangeRequest?.pointsBefore, nationalModelForTerminalData?.totalPoints)
    );
  };

  const hasPendingRankChanged = () => {
    return (
      isPendingChangeRequestReview() &&
      hasPendingValueChanged(pendingChangeRequest?.rankBefore, nationalModelForTerminalData?.terminalRank)
    );
  };

  const openChangeRequestDetails = () => {
    setIsChangeRequestDetailsOpen(true);
  };

  const closeChangeRequestDetails = () => {
    setIsChangeRequestDetailsOpen(false);
  };

  const applyAcceptedTerminalPresentation = async (nextTerminalID) => {
    setShowAcceptedTerminalPresentation(true);
    setPendingChangeRequest(null);
    setIsChangeRequestDetailsOpen(false);
    navigate(location.pathname, {
      replace: true,
      state: {
        terminalID: nextTerminalID,
        acceptedTerminalPresentation: true,
      },
    });
    await fetchData(nextTerminalID, 0);
  };

  const approvePendingChangeRequest = async () => {
    const requestID = pendingChangeRequest?.requestID;
    const userID = UtilsUnit.getUserID(userData);
    const userName = UtilsUnit.getUserName(userData);

    if (!requestID || !userID || !userName) {
      alert(ConstantsTexts.CODE_280);
      return;
    }

    showLoadingRef.current();
    setIsApprovingChangeRequest(true);

    try {
      const response = await ApiService.approveTerminalSubjectiveChangeRequest({
        requestID,
        userID,
        userName,
      });

      if (response?.status === 200) {
        const nextTerminalID =
          response?.data?.liveTerminalID ||
          pendingChangeRequest?.requestedTerminalID ||
          terminalID;
        await applyAcceptedTerminalPresentation(nextTerminalID);
        setSubjectiveChangeSuccessKind("approve");
        setIsSubjectiveChangeSuccessDialogOpen(true);
        window.dispatchEvent(new CustomEvent(PENDING_CHANGE_REQUESTS_CHANGED_EVENT));
      }
    } catch (error) {
      console.error("Error approving change request:", error);
    } finally {
      setIsApprovingChangeRequest(false);
      hideLoadingRef.current();
    }
  };

  const rejectPendingChangeRequest = async () => {
    const requestID = pendingChangeRequest?.requestID;
    const userID = UtilsUnit.getUserID(userData);
    const userName = UtilsUnit.getUserName(userData);

    if (!requestID || !userID || !userName) {
      alert(ConstantsTexts.CODE_282);
      return;
    }

    showLoadingRef.current();
    setIsRejectingChangeRequest(true);

    try {
      const response = await ApiService.rejectTerminalSubjectiveChangeRequest({
        requestID,
        userID,
        userName,
      });

      if (response?.status === 200) {
        const nextTerminalID =
          pendingChangeRequest?.baseTerminalID || pendingChangeRequest?.requestedTerminalID || terminalID;
        await applyAcceptedTerminalPresentation(nextTerminalID);
        setSubjectiveChangeSuccessKind("reject");
        setIsSubjectiveChangeSuccessDialogOpen(true);
        window.dispatchEvent(new CustomEvent(PENDING_CHANGE_REQUESTS_CHANGED_EVENT));
      }
    } catch (error) {
      console.error("Error rejecting change request:", error);
    } finally {
      setIsRejectingChangeRequest(false);
      hideLoadingRef.current();
    }
  };

  const isEditingInitialTerminalVersion = () => {
    return editMode && !isCreatingNewTerminalVersion && isTerminalVersionZero();
  };

  const isCreatingOrEditingInitialVersion = () => {
    return createNewTerminalMode || isEditingInitialTerminalVersion();
  };

  const shouldShowFieldsForNonInitialVersion = () => {
    return isTerminalVersionGreaterThanZero() || isCreatingNewTerminalVersion;
  };

  const shouldShowFieldsForNonInitialTerminalAndModelVersion = () => {
    return shouldShowFieldsForNonInitialVersion() || isModelVersionGreaterThanZero();
  };

  const shouldUseInitialVersionForManager = () => {
    return !createNewTerminalMode && !isCreatingNewTerminalVersion && UtilsUnit.managerUser(userData) && isTerminalVersionZero();
  };

  const isEditorEditingInitialVersion = () => {
    return UtilsUnit.editorUser(userData) && isEditingInitialTerminalVersion();
  };

  const isEditingExistingTerminal = () => {
    return editMode && !createNewTerminalMode;
  };

  const shouldShowFieldsForGeneral = () => {
    return shouldShowFieldsForNonInitialVersion() || isEditorEditingInitialVersion();
  };

  // Returns appropriate arrow image based on terminal rank comparison with father terminal rank
  const getTerminalRankArrowImage = () => {
    const terminalRank = Number(nationalModelForTerminalData.terminalRank);
    const fatherTerminalRank = nationalModelForTerminalData.fatherTerminalRank;

    if (terminalRank > fatherTerminalRank) {
      return ConstantsImages.RED_ARROW_IMAGE;
    } else if (terminalRank < fatherTerminalRank) {
      return ConstantsImages.GREEN_ARROW_IMAGE;
    } else {
      return ConstantsImages.WHITE_ARROW_IMAGE;
    }
  };

  // Returns arrow image dimensions based on whether terminal rank equals father terminal rank
  const getTerminalRankArrowImageSize = () => {
    const terminalRank = Number(nationalModelForTerminalData.terminalRank);
    const fatherTerminalRank = nationalModelForTerminalData.fatherTerminalRank;

    if (terminalRank === fatherTerminalRank) {
      return { widthIconProp: "2.5rem", heightIconProp: "1.2rem" };
    } else {
      return { widthIconProp: "1.2rem", heightIconProp: "3.3rem" };
    }
  };

  // Returns color based on terminal rank comparison with father terminal rank
  const getTerminalRankColor = () => {
    const terminalRank = Number(nationalModelForTerminalData.terminalRank);
    const fatherTerminalRank = nationalModelForTerminalData.fatherTerminalRank;

    if (terminalRank > fatherTerminalRank) {
      return "#EB6366";
    } else if (terminalRank < fatherTerminalRank) {
      return "#49DABF";
    } else {
      return "#FFFFFF";
    }
  };

  const handleCreateTerminalVersion = () => {
    handleSetEditMode(true);
    setIsCreatingNewTerminalVersion(true);
  };

  // Toggles base model selection mode, handles version 0 validation, and manages dropdown state
  const handleBaseModelChange = async () => {
    // Toggle the base model selected state
    const newIsBaseModelSelected = !isBaseModelSelected;

    // Only call this once
    const terminalVersionZero = terminalVersions?.find((version) => version.terminalVersionInt === 0);

    if (newIsBaseModelSelected) {
      setDropdownType(DropdownType.SINGLE_SELECT);

      // Store the original selected terminal version before changing it
      originalSelectedTerminalVersionRef.current = selectedTerminalVersion;
      setIsBaseModelSelected(true);

      if (terminalVersionZero) {
        setSelectedTerminalVersion(terminalVersionZero);
        // Reset the ref to version 0 when entering base model selection mode
        baseTerminalSelectedVersionRef.current = terminalVersionZero;
      }
    } else {
      // Check if the selected version is 0 and show alert if it is
      if (baseTerminalSelectedVersionRef.current?.terminalVersionInt === 0) {
        alert(ConstantsTexts.CODE_170);
        return;
      }

      // Set last action and show confirmation dialog
      lastActionRef.current = ConstantsParams.ActionTypes.BASE_MODEL;
      showEditAlertDialog();
      return;
    }
  };

  // Sends terminal data to API to make it the base model and refreshes data with version 0
  const handleConfirmBaseModelChange = async () => {
    try {
      showLoadingRef.current();

      const requestBody = {
        terminalID: baseTerminalSelectedVersionRef.current?.terminalID,
        userID: UtilsUnit.getUserID(userData),
        userName: UtilsUnit.getUserName(userData),
      };

      const response = await ApiService.makeTerminalAsZero(requestBody);

      if (response.status === 200) {
        const terminalIDZero = getTerminalIDZero();

        if (terminalIDZero) {
          // Automatically call fetchData again with terminalVersion = 0
          await fetchData(terminalIDZero, 0);
        }
      }
    } catch (error) {
      console.error("Error calling makeTerminalAsZero API:", error);
    } finally {
      hideLoadingRef.current();
    }
  };

  const handleBaseModelClose = () => {
    clearBaseModelAndSetOriginalSelectedTerminalVersion();
    UtilsUnit.clickOutside();
  };

  const clearBaseModel = () => {
    setDropdownType(DropdownType.DEFAULT_SELECT);
    setIsBaseModelSelected(false);
    originalSelectedTerminalVersionRef.current = null;
    baseTerminalSelectedVersionRef.current = null;
  };

  const clearBaseModelAndSetOriginalSelectedTerminalVersion = () => {
    if (originalSelectedTerminalVersionRef.current) {
      setSelectedTerminalVersion(originalSelectedTerminalVersionRef.current);
      clearBaseModel();
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (editMode) {
      handleSaveTerminalChanges();
    }
  };

  const shouldShowTerminalVersionCustomFooterItemSecond = () => {
    return UtilsUnit.managerUser(userData) && terminalVersions.length > 1;
  };

  const getDialogEditMessageText = () => {
    if (lastActionRef.current === ConstantsParams.ActionTypes.BASE_MODEL) {
      return ConstantsTexts.CODE_172.replace("{version}", baseTerminalSelectedVersionRef.current?.terminalVersionInt);
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.CANCEL) {
      return ConstantsTexts.CODE_59;
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.UPLOAD_POLYGON) {
      return ConstantsTexts.CODE_188;
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.DELETE) {
      return ConstantsTexts.CODE_205.replace("{terminalName}", nationalModelForTerminalData.terminalName).replace("{terminalMakat}", nationalModelForTerminalData.erezSKU);
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.DELETE_VERSION) {
      return ConstantsTexts.CODE_206.replace("{versionNumber}", terminalVersionToDeleteRef.current?.terminalVersionInt)
        .replace("{terminalName}", nationalModelForTerminalData.terminalName)
        .replace("{terminalMakat}", nationalModelForTerminalData.erezSKU);
    }
  };

  const getDialogEditApproveText = () => {
    if (lastActionRef.current === ConstantsParams.ActionTypes.BASE_MODEL) {
      return ConstantsTexts.CODE_174;
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.CANCEL) {
      return ConstantsTexts.CODE_60;
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.UPLOAD_POLYGON) {
      return ConstantsTexts.CODE_189;
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.DELETE || lastActionRef.current === ConstantsParams.ActionTypes.DELETE_VERSION) {
      return ConstantsTexts.CODE_207;
    }
  };

  const getDialogEditCancelText = () => {
    if (
      lastActionRef.current === ConstantsParams.ActionTypes.BASE_MODEL ||
      lastActionRef.current === ConstantsParams.ActionTypes.UPLOAD_POLYGON ||
      lastActionRef.current === ConstantsParams.ActionTypes.DELETE ||
      lastActionRef.current === ConstantsParams.ActionTypes.DELETE_VERSION
    ) {
      return ConstantsTexts.CODE_7;
    } else if (lastActionRef.current === ConstantsParams.ActionTypes.CANCEL) {
      return ConstantsTexts.CODE_61;
    }
  };

  // Helper function to check if base model is selected and original selected terminal version ref is not null
  const isBaseModelSelectedAndOriginalSelectedTerminalVersionRef = () => {
    return isBaseModelSelected && originalSelectedTerminalVersionRef.current;
  };

  // Helper function to get the appropriate terminal version (original or selected)
  const getAppropriateTerminalVersion = () => {
    return isBaseModelSelectedAndOriginalSelectedTerminalVersionRef() ? originalSelectedTerminalVersionRef.current : selectedTerminalVersion;
  };

  // Helper function to find terminal version with version 0 and get its terminalID
  const getTerminalIDZero = () => {
    const terminalVersionZero = terminalVersions?.find((version) => version.terminalVersionInt === 0);
    return terminalVersionZero?.terminalID;
  };

  // Polygon upload functionality
  const handlePolygonUploadClick = () => {
    lastActionRef.current = ConstantsParams.ActionTypes.UPLOAD_POLYGON;
    setDialogEditOpen(true);
  };

  const handlePolygonUploadConfirm = () => {
    // Create a hidden file input
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".geojson";
    fileInput.style.display = "none";

    fileInput.onchange = async (event) => {
      const file = event.target.files[0];

      if (!file) {
        return;
      }

      try {
        showLoadingRef.current();

        const result = await PolygonUploadUtils.processPolygonFile(file);

        if (result.success) {
          // Set the polygon data to jsonEditRef as a JSON string
          jsonEditRef.current.polygon = JSON.stringify(result.geojson);
          // Update the uploaded polygon state
          setUploadedPolygon(result.geojson);
        } else {
          // Show error message based on error type
          let errorMessage = ConstantsTexts.CODE_191;

          switch (result.error) {
            case ERROR_MESSAGES.MULTIPLE_FILES:
              errorMessage = ConstantsTexts.CODE_192;
              break;
            case ERROR_MESSAGES.FILE_TOO_LARGE:
              errorMessage = ConstantsTexts.CODE_193;
              break;
            case ERROR_MESSAGES.INVALID_FORMAT:
              errorMessage = ConstantsTexts.CODE_194;
              break;
            case ERROR_MESSAGES.INVALID_GEOMETRY_TYPE:
              errorMessage = ConstantsTexts.CODE_195;
              break;
            case ERROR_MESSAGES.INVALID_COORDINATE_SYSTEM:
              errorMessage = ConstantsTexts.CODE_196;
              break;
            default:
              errorMessage = ConstantsTexts.CODE_191;
          }

          alert(errorMessage);
        }
      } catch (error) {
        console.error("Error processing polygon file:", error);
        alert(ConstantsTexts.CODE_191);
      } finally {
        hideLoadingRef.current();
      }
    };

    // Trigger file selection
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  };

  // Unified function to handle both terminal and terminal version deletion
  const handleDeleteTerminalOrVersion = async (terminalID, isTerminalVersion = false) => {
    try {
      showLoadingRef.current();

      const requestBody = {
        terminalID: terminalID,
        userID: UtilsUnit.getUserID(userData),
        userName: UtilsUnit.getUserName(userData),
      };

      const response = await ApiService.deleteTerminal(requestBody);

      if (response.status === 200) {
        if (isTerminalVersion) {
          // For terminal version deletion - refresh the data to update the terminal versions list
          const terminalIDZero = getTerminalIDZero();

          if (terminalIDZero) {
            // Automatically call fetchData again with terminalVersion = 0
            await fetchData(terminalIDZero, 0);
            UtilsUnit.clickOutside();
          }
        } else {
          // For terminal deletion - navigate back to dashboard
          navigate(-1);
        }
      }
    } catch (error) {
      console.error("Error deleting terminal:", error);
    } finally {
      hideLoadingRef.current();
    }
  };

  // Handles delete terminal button click - shows confirmation dialog
  const handleDeleteTerminalClick = () => {
    lastActionRef.current = ConstantsParams.ActionTypes.DELETE;
    setDialogEditOpen(true);
  };

  // Handles delete terminal version button click - shows confirmation dialog
  const handleDeleteTerminalVersionClick = (terminalVersion) => {
    terminalVersionToDeleteRef.current = terminalVersion;
    lastActionRef.current = ConstantsParams.ActionTypes.DELETE_VERSION;
    setDialogEditOpen(true);
  };

  // Clear handlers for dropdowns
  const handleClearTerminalType = () => {
    setSelectedTerminalTypeName(null);
    if (jsonEditRef.current.terminal_type) {
      delete jsonEditRef.current.terminal_type;
    }
  };

  const handleClearTerminalOwnership = () => {
    setSelectedTerminalOwnership(null);
    if (jsonEditRef.current.terminal_ownership) {
      delete jsonEditRef.current.terminal_ownership;
    }
  };

  const handleClearMunicipalityPosition = () => {
    setSelectedMunicipalityPositionPlace(null);
    if (jsonEditRef.current.municipality_position_place) {
      delete jsonEditRef.current.municipality_position_place;
    }
  };

  const handleClearWorkNeed = () => {
    setSelectedWorkNeed(null);
    if (jsonEditRef.current.work_need) {
      delete jsonEditRef.current.work_need;
    }
  };

  const handleClearUrgencyMeasure = () => {
    setSelectedUrgencyMeasureValue(null);
    if (jsonEditRef.current.year_of_need_grade) {
      delete jsonEditRef.current.year_of_need_grade;
    }
  };

  const handleClearOrganizationInCharge = () => {
    setSelectedOrganizationInChargeName(null);
    if (jsonEditRef.current.organization_in_charge) {
      delete jsonEditRef.current.organization_in_charge;
    }
  };

  const handleClearBudgetStatus = () => {
    setSelectedBudgetStatusName(null);
    if (jsonEditRef.current.budget_status) {
      delete jsonEditRef.current.budget_status;
    }
  };

  const handleClearTerminalStatus = () => {
    setSelectedTerminalStatusName(null);
    if (jsonEditRef.current.status) {
      delete jsonEditRef.current.status;
    }
  };

  const handleClearApplicability = () => {
    setSelectedApplicability(null);
    if (jsonEditRef.current.applicability) {
      delete jsonEditRef.current.applicability;
    }
  };

  const handleClearRingByStrategicPlan = () => {
    setSelectedRingByStrategicPlan(null);
    if (jsonEditRef.current.ring_by_strategic_plan) {
      delete jsonEditRef.current.ring_by_strategic_plan;
    }
  };

  return {
    dataIsReceived,
    editMode,
    createNewTerminalMode,
    setCreateNewTerminalMode,
    colorEditMode,
    handleSearchTerminalIconClick,
    handleSetEditModeTrueClick,
    showUpdatesList,
    handleShowUpdatesListClick,
    changedTerminalID,
    nationalModelForTerminalData,
    terminalOwnership,
    municipalityPosition,
    terminalTypesAndPrices,
    handleSelectTerminalTypeName,
    selectedTerminalTypeName,
    handleSelectTerminalOwnership,
    selectedTerminalOwnership,
    handleSelectMunicipalityPositionPlace,
    selectedMunicipalityPositionPlace,
    nationalModelForTerminalCount,
    nationalModelForTerminalDistinctWorkneed,
    handleSelectWorkNeed,
    selectedWorkNeed,
    inputValueTerminalMakat,
    handleChangeTerminalMakat,
    inputValueTerminalMakatErez,
    handleChangeTerminalMakatErez,
    inputValueTerminalName,
    handleChangeTerminalName,
    inputValueRequiredTerminalSize,
    handleChangeRequiredTerminalSize,
    inputValueAreaInPlaningArea,
    handleChangeAreaInPlaningArea,
    inputValueAreaNeedForPlaningArea,
    handleChangeAreaNeedForPlaningArea,
    organizationInCharge,
    handleSelectOrganizationInChargeName,
    selectedOrganizationInChargeName,
    budgetStatus,
    handleSelectBudgetStatusName,
    selectedBudgetStatusName,
    terminalStatus,
    handleSelectTerminalStatusName,
    selectedTerminalStatusName,
    terminalStops,
    numbers1To5,
    handleSelectApplicability,
    selectedApplicability,
    getSecondaryMeasurePercentForDisplay,
    inputValueImportanceMeasure,
    handleChangeImportanceMeasure,
    inputValuePotentialMeasure,
    handleChangePotentialMeasure,
    numbers1To4,
    handleSelectRingByStrategicPlan,
    selectedRingByStrategicPlan,
    isDialogEditOpen,
    closeEditAlertDialog,
    getDialogEditMessageText,
    getDialogEditApproveText,
    getDialogEditCancelText,
    urgencyMeasure,
    handleSelectUrgencyMeasureValue,
    selectedUrgencyMeasureValue,
    inputValueComments,
    handleChangeComments,
    handleCancelTerminalChanges,
    uniqueDistricts,
    handleSelectDropdownDistrict,
    selectedDistrict,
    uniqueMunicipalities,
    handleSelectDropdownMunicipality,
    selectedMunicipality,
    uniqueTerminals,
    handleSelectDropdownTerminal,
    selectedTerminal,
    showSearchTerminal,
    busLinesMenuOpened,
    handleBusLinesMenuOpened,
    busLinesInTerminalChecked,
    handleBusLinesInTerminalChecked,
    busLinesInTerminalExpanded,
    handleBusLinesInTerminalExpanded,
    busLinesNearTerminalChecked,
    handleBusLinesNearTerminalChecked,
    busLinesNearTerminalExpanded,
    handleBusLinesNearTerminalExpanded,
    busLinesInTerminalItems,
    busLinesNearTerminalItems,
    handleTerminalItemCheck,
    handleNearTerminalItemCheck,
    getSelectedBusLinesData,
    userData,
    modelVersions,
    handleSelectModelVersion,
    selectedModelVersion,
    terminalVersions,
    handleSelectTerminalVersion,
    selectedTerminalVersion,
    modelTerminalVersions,
    isTerminalVersionGreaterThanZeroAndEditModeFalse,
    isCreatingOrEditingInitialVersion,
    isTerminalVersionGreaterThanZero,
    shouldShowSourceTerminalScore,
    shouldUseTerminalVersionComparisonColors,
    shouldShowFieldsForNonInitialTerminalAndModelVersion,
    getTerminalRankArrowImage,
    getTerminalRankArrowImageSize,
    getTerminalRankColor,
    handleCreateTerminalVersion,
    handleFormSubmit,
    handleBaseModelChange,
    handleBaseModelClose,
    isBaseModelSelected,
    dropdownType,
    shouldShowTerminalVersionCustomFooterItemSecond,
    shouldUseInitialVersionForManager,
    isEditorEditingInitialVersion,
    isTerminalVersionZero,
    isEditingExistingTerminal,
    shouldShowFieldsForGeneral,
    handlePolygonUploadClick,
    uploadedPolygon,
    handleDeleteTerminalClick,
    handleDeleteTerminalVersionClick,
    lastActionRef,
    handleClearTerminalType,
    handleClearTerminalOwnership,
    handleClearMunicipalityPosition,
    handleClearWorkNeed,
    handleClearUrgencyMeasure,
    handleClearOrganizationInCharge,
    handleClearBudgetStatus,
    handleClearTerminalStatus,
    handleClearApplicability,
    handleClearRingByStrategicPlan,
    isFrozenTerminalBlockedDialogOpen,
    closeFrozenTerminalBlockedDialog,
    showFrozenTerminalBlockedDialog,
    isSubjectiveChangeReasonDialogOpen,
    closeSubjectiveChangeReasonDialog,
    submitSubjectiveChangeRequest,
    isSubjectiveChangeSuccessDialogOpen,
    closeSubjectiveChangeSuccessDialog,
    getSubjectiveChangeSuccessMessage,
    subjectiveChangeSuccessKind,
    isOpenChangeRequestBlockedDialogOpen,
    closeOpenChangeRequestBlockedDialog,
    getOpenChangeRequestBlockedTerminalName,
    pendingChangeRequest,
    isPendingChangeRequestReview,
    getPendingUrgencyGaugePresentation,
    getPendingFeasibilityBefore,
    hasPendingUrgencyChanged,
    hasPendingFeasibilityChanged,
    hasPendingPointsChanged,
    hasPendingRankChanged,
    isChangeRequestDetailsOpen,
    openChangeRequestDetails,
    closeChangeRequestDetails,
    approvePendingChangeRequest,
    isApprovingChangeRequest,
    rejectPendingChangeRequest,
    isRejectingChangeRequest,
  };
}
